import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FormFieldProps {
  id: string;
  label: string;
  children: ReactNode;
  error?: string | undefined;
  description?: string | undefined;
  required?: boolean;
  className?: string;
}

export function FormField({
  id,
  label,
  children,
  error,
  description,
  required = false,
  className,
}: FormFieldProps): JSX.Element {
  const describedBy = [
    description === undefined ? null : `${id}-description`,
    error === undefined ? null : `${id}-error`,
  ]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-invalid={error !== undefined}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </Label>
      <div aria-describedby={describedBy === '' ? undefined : describedBy}>{children}</div>
      {description === undefined ? null : (
        <p id={`${id}-description`} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error === undefined ? null : (
        <p id={`${id}-error`} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
